import React, { useState } from 'react';
import { X, Calculator, TrendingUp, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { corporateFinanceStore, ForecastScenario } from '../../services/finance/corporateFinanceStore';

interface CorporateFinanceScenarioStudioProps {
  onClose: () => void;
}

export const CorporateFinanceScenarioStudio: React.FC<CorporateFinanceScenarioStudioProps> = ({ onClose }) => {
  const [name, setName] = useState('Scenario Custom — Expansion LatAm & API Paid');
  const [trafficGrowthRatePct, setTrafficGrowthRatePct] = useState(20);
  const [adRpmAverageEur, setAdRpmAverageEur] = useState(17.5);
  const [affiliateConversionPct, setAffiliateConversionPct] = useState(3.5);
  const [hypotheticalPaidConversionPct, setHypotheticalPaidConversionPct] = useState(2.0);
  const [isMonetizationMasterSwitchOn, setIsMonetizationMasterSwitchOn] = useState(false);
  const [notes, setNotes] = useState('Modélisation prospective. Ne modifie pas les revenus réels encaissés.');

  const [savedNotice, setSavedNotice] = useState(false);

  // Derived calculations
  const projectedAnnualRevenueEur = Math.round(
    2180000 * (1 + trafficGrowthRatePct / 100) * (adRpmAverageEur / 16.5) +
      (isMonetizationMasterSwitchOn ? hypotheticalPaidConversionPct * 650000 : 0)
  );

  const projectedGrossMarginPct = isMonetizationMasterSwitchOn ? 88.0 : 86.5;
  const projectedMonthlyBurnEur = Math.round(38000 - (projectedAnnualRevenueEur / 12) * 0.15);
  const projectedRunwayMonths = Math.min(60, Math.round((1218500 / Math.max(10000, projectedMonthlyBurnEur)) * 10) / 10);

  const handleSaveScenario = () => {
    corporateFinanceStore.addScenario({
      name,
      type: 'UPSIDE',
      trafficGrowthRatePct,
      adRpmAverageEur,
      affiliateConversionPct,
      hypotheticalPaidConversionPct,
      projectedAnnualRevenueEur,
      projectedGrossMarginPct,
      projectedMonthlyBurnEur,
      projectedRunwayMonths,
      isMonetizationMasterSwitchOn,
      notes
    });

    setSavedNotice(true);
    setTimeout(() => {
      onClose();
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full border border-slate-200 shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
              <Calculator className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                Studio de Modélisation Financière & Scénarios Prospection
              </h2>
              <p className="text-xs text-slate-500 font-mono">YamooPay FP&A Scenario Engine (Prompt 30)</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 rounded-lg text-slate-500 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5 text-sm">
          {savedNotice && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 font-bold text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              Scénario financier ajouté avec succès aux prévisions !
            </div>
          )}

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs font-semibold">
            <div className="font-bold uppercase flex items-center gap-1.5 mb-1 text-amber-800">
              <ShieldCheck className="w-4 h-4 text-amber-600" />
              Règle de Séparation Absolue (Prompt 30 & Prompt 21)
            </div>
            La modélisation de revenus payants n&apos;active AUCUN flux de paiement Stripe/Moneroo et ne modifie JAMAIS les revenus réels comptabilisés.
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">Nom du Scénario</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-2.5 rounded-lg border border-slate-300 bg-slate-50 focus:bg-white text-xs font-bold"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">Croissance Trafic (%)</label>
              <input
                type="number"
                value={trafficGrowthRatePct}
                onChange={(e) => setTrafficGrowthRatePct(parseFloat(e.target.value) || 0)}
                className="w-full p-2 rounded border border-slate-300 text-xs"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">RPM / EPMV Moyen (€)</label>
              <input
                type="number"
                step="0.5"
                value={adRpmAverageEur}
                onChange={(e) => setAdRpmAverageEur(parseFloat(e.target.value) || 0)}
                className="w-full p-2 rounded border border-slate-300 text-xs"
              />
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-800 uppercase">Simuler Master Switch Paid Monetization = ON ?</span>
              <input
                type="checkbox"
                checked={isMonetizationMasterSwitchOn}
                onChange={(e) => setIsMonetizationMasterSwitchOn(e.target.checked)}
                className="w-4 h-4 text-emerald-600 rounded cursor-pointer"
              />
            </div>

            {isMonetizationMasterSwitchOn && (
              <div>
                <label className="block text-xs font-bold text-purple-800 mb-1 uppercase">Taux de Conversion Hypothétique (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={hypotheticalPaidConversionPct}
                  onChange={(e) => setHypotheticalPaidConversionPct(parseFloat(e.target.value) || 0)}
                  className="w-full p-2 rounded border border-purple-300 bg-purple-50/30 text-xs font-bold text-purple-900"
                />
              </div>
            )}
          </div>

          <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-200 space-y-2 text-xs">
            <div className="font-bold text-emerald-900 uppercase">Impact Financier Projeté (Scénario)</div>
            <div className="grid grid-cols-3 gap-2 text-slate-800">
              <div>Revenu Annuel: <strong className="block text-sm text-emerald-700">{projectedAnnualRevenueEur.toLocaleString()} €</strong></div>
              <div>Net Burn Mensuel: <strong className="block text-sm text-emerald-700">{projectedMonthlyBurnEur.toLocaleString()} €</strong></div>
              <div>Runway Estimé: <strong className="block text-sm text-emerald-700">{projectedRunwayMonths} mois</strong></div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 uppercase">Hypothèses & Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full p-2 rounded border border-slate-300 text-xs"
            />
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 text-xs font-bold hover:bg-slate-100 transition-colors cursor-pointer"
          >
            Annuler
          </button>
          <button
            onClick={handleSaveScenario}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
          >
            Enregistrer le Scénario
          </button>
        </div>
      </div>
    </div>
  );
};
